<template>
  <div class="d-flex">
    <b-container fluid class="p-0">
      <b-row no-gutters>
        <b-col cols="3" class="pr-3">
          <slot name="ruleID" :rule="rule" :options="ruleIDOptions">
            <v-select
              :readonly="!!typeLabel"
              v-model="rule.id"
              :items="ruleIDOptions"
              item-text="title"
              item-value="name"
              dense
              outlined
            />
          </slot>
        </b-col>
        <b-col cols="4" class="pr-3">
          <slot name="ruleOperator" :rule="rule" :options="ruleOperatorOptions">
            <v-select
              :readonly="!!typeLabel"
              v-model="rule.operator"
              :label="$t('condition')"
              :items="ruleOperatorOptions"
              item-text="title"
              item-value="name"
              dense
              outlined
            />
          </slot>
        </b-col>

        <b-col cols="5" class="pr-3 custom-rule-select" v-if="ruleParams">
          <slot
            name="dropdown_multiple"
            :rule="rule"
            :multiple="multiple"
            :options="ruleParams.options"
            v-if="
              ruleParams.type === 'dropdown_multi_search' ||
              (ruleParams.type === 'dropdown_multi_search_area' &&
                rule.operator !== 'is_empty')
            "
          >
            <v-combobox
              :readonly="!!typeLabel"
              v-model="rule.value"
              :label="ruleParams.title"
              :items="ruleParams.options"
              item-text="text"
              return-object
              cache-items
              dense
              outlined
              :clearable="!typeLabel"
              :multiple="multiple"
              small-chips
              deletable-chips
              hide-no-data
              hide-selected
              :placeholder="$t('enter_at_least_2_characters')"
              :loading="isLoading"
              :search-input.sync="search"
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            />
          </slot>

          <slot
            name="number"
            :rule="rule"
            v-else-if="ruleParams.type === 'number'"
          >
            <v-text-field
              :readonly="!!typeLabel"
              v-model="rule.value"
              :label="ruleParams.title"
              dense
              outlined
              :clearable="!typeLabel"
              type="number"
              hide-details
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
              @change="() => validateMinValue('value', 0, 'rule')"
            />
          </slot>

          <slot
            name="text"
            :rule="rule"
            v-else-if="
              ruleParams.type === 'text' && rule.operator !== 'is_empty'
            "
          >
            <v-text-field
              :readonly="!!typeLabel"
              v-model="rule.value"
              :label="ruleParams.title"
              dense
              outlined
              :clearable="!typeLabel"
              hide-details
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            />
          </slot>

          <slot
            name="dropdown"
            :rule="rule"
            v-else-if="
              ruleParams.type === 'dropdown' || ruleParams.type === 'dropdown2'
            "
          >
            <v-autocomplete
              :readonly="!!typeLabel"
              v-model="rule.value"
              :label="ruleParams.title"
              :items="ruleParams.values"
              item-text="text"
              item-value="index"
              return-object
              dense
              outlined
              :clearable="!typeLabel"
              hide-details
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            />
          </slot>

          <slot
            name="dropdown"
            :rule="rule"
            v-else-if="
              (ruleParams.type === 'dropdown_multi_select' ||
                ruleParams.type === 'dropdown_multi_select_area' ||
                ruleParams.type === 'dropdown_multi_select_with_empty') &&
              rule.operator !== 'is_empty'
            "
          >
            <v-autocomplete
              :readonly="!!typeLabel"
              v-model="rule.value"
              :label="ruleParams.title"
              :items="ruleParams.values"
              item-text="text"
              item-value="index"
              small-chips
              deletable-chips
              return-object
              multiple
              dense
              outlined
              :clearable="!typeLabel"
              hide-details
              :error-messages="ruleValueErrors"
              @blur="$v.rule.value.$touch()"
            >
              <template v-slot:prepend-item>
                <v-list-item ripple @mousedown.prevent @click="toggle">
                  <v-list-item-action>
                    <v-icon
                      :color="rule.value.length > 0 ? 'purple darken-2' : ''"
                    >
                      {{
                        rule.value.length === 0
                          ? "mdi-checkbox-blank-outline"
                          : rule.value.length === ruleParams.values.length
                          ? "mdi-close-box"
                          : "mdi-minus-box"
                      }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title> Select All </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
              <template v-slot:selection="{ attrs, item, parent, selected }">
                <v-chip
                  v-if="item === Object(item)"
                  v-bind="attrs"
                  :input-value="selected"
                  label
                  small
                >
                  <span class="pr-2">
                    {{ item.text }}
                  </span>
                  <v-icon small @click="parent.selectItem(item)">
                    $delete
                  </v-icon>
                </v-chip>
              </template>
            </v-autocomplete>
          </slot>
          <slot
            name="dropdown"
            :rule="rule"
            v-else-if="ruleParams.type === 'time_range'"
          >
            <div class="row ma-0 pa-0">
              <div class="col-6 ma-0 pa-0 pr-3">
                <v-select
                  :readonly="!!typeLabel"
                  v-model="rangeValue[0]"
                  item-text="text"
                  item-value="index"
                  :items="[
                    // { index: 0, text: ' 0 am' },
                    ...Array.from({ length: 24 }, (_, i) => ({
                      index: i,
                      text: `${i % 12 != 0 ? i % 12 : i != 0 ? 12 : 0} ${
                        i < 12 ? 'am' : 'pm'
                      }`,
                    })),
                  ]"
                  placeholder="Start time"
                  dense
                  outlined
                  :clearable="!typeLabel"
                  :error-messages="ruleValueErrors"
                  @blur="$v.rule.value.$touch()"
                />
              </div>
              <div class="col-6 ma-0 pa-0 pr-3">
                <v-select
                  :readonly="!!typeLabel"
                  v-model="rangeValue[1]"
                  :items="timeRangeEndValue"
                  placeholder="End time"
                  item-text="text"
                  item-value="index"
                  dense
                  outlined
                  :clearable="!typeLabel"
                  :error-messages="ruleValueErrors"
                  @blur="$v.rule.value.$touch()"
                />
              </div>
            </div>
          </slot>

          <slot
            name="dropdown"
            :rule="rule"
            v-else-if="ruleParams.type === 'date'"
          >
            <SingleDatepicker
              position="right"
              :value="rule.value"
              :clearDate="
                () => {
                  rule.value = null;
                }
              "
              @on-submit="
                (date) => {
                  rule.value = date[0];
                }
              "
              class="poppins"
              :max-date="null"
              :min-date="new Date().toLocaleDateString()"
              customStyle="d-flex align-items-center"
            >
              <template v-slot:custom-input="{ inputValue }">
                <v-text-field
                  :value="inputValue"
                  dense
                  outlined
                  :label="$t('select_a_date')"
                  class="w-100"
                >
                  <template v-slot:prepend-inner>
                    <span class="svg-icon svg-icon-2 mb-0 mr-2">
                      <v-icon size="22">mdi-calendar</v-icon>
                    </span>
                  </template>
                </v-text-field>
              </template>
            </SingleDatepicker>
          </slot>
        </b-col>
      </b-row>
    </b-container>
    <div class="max-h-65" v-if="!typeLabel">
      <button
        class="h-40px w-40px btn btn-sm btn-icon btn-light-info btn-hover-light"
        @click="deleteRule"
      >
        <span class="svg-icon">
          <v-icon size="18">mdi-delete</v-icon>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { getToken } from "@/core/services/jwt.service";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import { SET_NOT_VALID } from "@/core/services/store/omsRules.module";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";
import SingleDatepicker from "@/own/components/SingleDatepicker.vue";

export default {
  name: "Rule",
  components: { SingleDatepicker },
  mixins: [validationMixin, fieldValueValidation],
  validations() {
    return this.rule.operator !== "is_empty"
      ? {
          rule: {
            value: { required },
          },
        }
      : {
          rule: {},
        };
  },
  props: {
    rule: {
      type: Object,
      required: true,
    },
    options: {
      type: Array,
    },
    userId: {
      required: false,
    },
    typeLabel: {
      // usage only for oms rule history and to disable/readonly all btns/input
      type: String,
      required: false,
      default: null,
    },
  },
  mounted() {
    if (this.rule.operator !== "is_empty") {
      this.$v.rule.value.$touch();
    } else {
      this.$store.commit(SET_NOT_VALID, false);
    }

    if (
      this.ruleParams.name === "city" ||
      this.ruleParams.name === "country" ||
      this.ruleParams.name === "area" ||
      this.ruleParams.name === "sender_city" ||
      this.ruleParams.name === "sender_country" ||
      this.ruleParams.name === "sender_area" ||
      this.ruleParams.name === "receiver_city" ||
      this.ruleParams.name === "receiver_country" ||
      this.ruleParams.name === "receiver_area"
    ) {
      // console.log('rule',this.rule)
      this.ruleParams.options = this.rule.value;
      // this.search = this.rule.value[0].text;
      this.$store.commit(SET_NOT_VALID, false);
    }
    if (this.ruleParams.type == "time_range") {
      this.rangeValue = Array.from(
        String(this.rule.value).match(/\d+/g),
        Number
      );
    }
  },
  data: () => ({
    search: null,
    isLoading: false,
    doneTypingInterval: 1500,
    typingTimer: null,
    rangeValue: [0, 1],
  }),
  computed: {
    ruleParams() {
      let res;
      res = this.options.find((option) => option.name === this.rule.id);
      // console.log("res", res);
      return res;
    },
    ruleIDOptions() {
      const options = this.options;
      return options.map(function (option) {
        return {
          name: option.name,
          title: option.title,
        };
      });
    },
    currentRuleID: function () {
      return this.rule.id;
    },
    currentRuleOperator: function () {
      return this.rule.operator;
    },
    multiple: function () {
      return this.rule.operator.includes("in");
    },
    ruleOperatorOptions: function () {
      if (this.ruleParams) {
        if (this.ruleParams?.operators) {
          return this.ruleParams.operators;
        } else {
          switch (this.ruleParams.type) {
            case "dropdown_multi_search":
              return [
                { name: "in", title: "belongs to" },
                { name: "not_in", title: "doesn't belong to" },
                { name: "equal", title: "equals" },
              ];
            case "dropdown_multi_select":
              return [
                { name: "in", title: "belongs to" },
                { name: "not_in", title: "doesn't belong to" },
                { name: "equal", title: "equals" },
              ];
            case "dropdown_multi_search_area":
              return [
                { name: "in", title: "belongs to" },
                { name: "not_in", title: "doesn't belong to" },
                { name: "equal", title: "equals" },
                { name: "is_empty", title: "is empty" },
              ];
            case "dropdown_multi_select_with_empty":
              return [
                { name: "in", title: "belongs to" },
                { name: "not_in", title: "doesn't belong to" },
                { name: "equal", title: "equals" },
                { name: "not_equal", title: "not equal" },
                { name: "is_empty", title: "is empty" },
              ];
            case "text":
              return [
                { name: "equal", title: "equals" },
                { name: "not_equal", title: "not equal" },
                { name: "like", title: "like" },
                { name: "is_empty", title: "is empty" },
              ];
            case "time_range":
              return [{ name: "in_between", title: "in between" }];
            case "number":
              return [
                { name: "equal", title: "equals" },
                { name: "not_equal", title: "not equal" },
                { name: "greater_than", title: "greater than" },
                { name: "less_than", title: "less than" },
              ];
            case "dropdown":
              return [{ name: "equal", title: "equals" }];
            case "dropdown2":
              return [
                { name: "in", title: "belongs to" },
                { name: "not_in", title: "doesn't belong to" },
                { name: "equal", title: "equals" },
              ];
            default:
              return [
                { name: "in", title: "belongs to" },
                { name: "not_in", title: "doesn't belong to" },
                { name: "equal", title: "equals" },
                { name: "not_equal", title: "not equal" },
                { name: "greater_than", title: "greater than" },
                { name: "less_than", title: "less than" },
                { name: "like", title: "like" },
              ];
          }
        }
      } else return [];
    },
    ruleValueErrors: function () {
      return this.rule.operator !== "is_empty"
        ? this.handleFormValidation("value")
        : [];
    },
    timeRangeEndValue() {
      return Array.from({ length: 24 }, (_, i) => ({
        index: i,
        text: `${i % 12 != 0 ? i % 12 : i != 0 ? 12 : 0} ${
          i < 12 ? "am" : "pm"
        }`,
      })).slice(this.rangeValue[0] + 1);
    },
  },
  methods: {
    toggle() {
      this.$nextTick(() => {
        if (this.rule.value.length === this.ruleParams.values.length) {
          this.rule.value = [];
        } else {
          this.rule.value = this.ruleParams.values.map((value) => value.index);
        }
      });
    },
    handleFormValidation(fieldName) {
      const errors = [];
      if (!this.$v.rule[fieldName].$dirty) return errors;
      if ("required" in this.$v.rule[fieldName]) {
        !this.$v.rule[fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      return errors;
    },
    resetRuleValue: function () {
      if (this.multiple && this.ruleParams.type === "dropdown_multiple") {
        this.rule.value = [];
      } else {
        this.rule.value = "";
      }
      if (
        this.ruleParams.hasOwnProperty("options") &&
        this.ruleParams.options.length
      ) {
        this.ruleParams.options = [];
      }
    },
    deleteRule: function () {
      const self = this;
      const parentRules = self.$parent.currentQuery.rules;
      parentRules.splice(parentRules.indexOf(self.rule), 1);
    },
    async getData(value) {
      let requestBody = { q: value };
      if (this.userId && this.ruleParams.name === "sku")
        requestBody.clients = this.userId.map((item) => item.index);

      await fetch(
        `${process.env.VUE_APP_BASE_URL}${this.ruleParams.search_url}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(requestBody),
        }
      )
        .then((res) => res.json())
        .then((res) => {
          if (this.ruleParams.name === "sku" && res.skus && this.userId) {
            this.ruleParams.options = res.skus.map((item) => ({
              index: item.skuId,
              text: item.systemCode,
            }));
          } else if (this.ruleParams.name === "country" && res.countries) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.countries,
              ]),
            ];
          } else if (this.ruleParams.name === "city" && res.cities) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.cities,
              ]),
            ];
          } else if (this.ruleParams.name === "area" && res.areas) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.areas,
              ]),
            ];
          } else if (
            this.ruleParams.name === "sender_country" &&
            res.countries
          ) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.countries,
              ]),
            ];
          } else if (this.ruleParams.name === "sender_city" && res.cities) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.cities,
              ]),
            ];
          } else if (this.ruleParams.name === "sender_area" && res.areas) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.areas,
              ]),
            ];
          } else if (
            this.ruleParams.name === "receiver_country" &&
            res.countries
          ) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.countries,
              ]),
            ];
          } else if (this.ruleParams.name === "receiver_city" && res.cities) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.cities,
              ]),
            ];
          } else if (this.ruleParams.name === "receiver_area" && res.areas) {
            this.ruleParams.options = [
              ...new Set([
                ...(this.ruleParams.options ? this.ruleParams.options : []),
                ...res.areas,
              ]),
            ];
          }
        })
        .finally(() => (this.isLoading = false));
    },
  },
  watch: {
    rangeValue: {
      handler() {
        if (this.ruleParams.type === "time_range") {
          this.rule.value = this.rangeValue.join("-");
        }
      },
      deep: true,
    },
    currentRuleID: function () {
      this.resetRuleValue();
    },
    currentRuleOperator: function (to, from) {
      if (to.includes("in") !== from.includes("in")) {
        this.resetRuleValue();
      }
      return false;
    },
    search(val) {
      clearTimeout(this.typingTimer);
      if (!val || val === "") {
        return;
      }
      this.typingTimer = setTimeout(() => {
        this.isLoading = true;
        this.getData(val.trim());
      }, this.doneTypingInterval);
    },
    rule: {
      handler(newValue) {
        if (Array.isArray(newValue.value)) {
          if (newValue.value.length) this.$store.commit(SET_NOT_VALID, false);
          else this.$store.commit(SET_NOT_VALID, true);
        } else {
          if (newValue.value) this.$store.commit(SET_NOT_VALID, false);
          else {
            if (newValue.operator === "is_empty")
              this.$store.commit(SET_NOT_VALID, false);
            else this.$store.commit(SET_NOT_VALID, true);
          }
        }
      },
      deep: true,
    },
  },
};
</script>
